import type { ClientViewFilter } from "./client-list";
import type { ScreeningSubject } from "./compliance-review";
// ─── Trapets Search Types ────────────────────────────────

export type TrapetsSearchType = "ssn" | "name" | "company";

export type TrapetsIndividual = {
  [key: string]: any;
};

export type TrapetsSearchResult = {
  Id: string;
  SubQueryId?: string;
  Individuals?: TrapetsIndividual[];
  Entities?: any[];
  [key: string]: any;
};

/** The three tenant Trapets modes. Credentials live in the platform env; mode selects which set. */
export type TrapetsMode = "test" | "production" | "disabled";

/** GET /internal/trapets — the tenant's current Trapets mode (mode-only contract). */
export type TrapetsSettingsResponse = { trapetsMode: TrapetsMode };

/** PATCH /internal/trapets — set the tenant's Trapets mode. */
export type TrapetsSettingsUpdateRequest = { trapetsMode: TrapetsMode };

export type TrapetsSearchResponse = {
  success: boolean;
  results?: TrapetsSearchResult[];
  /** On failure: the reason, in the request language. */
  error?: string;
  /** On failure: the stable error code (QL-718), e.g. `trapets_disabled`. */
  code?: string;
  isTestMode: boolean;
};

/** GET /internal/trapets/services — Trapets' list of services, or why it couldn't be read. */
export type TrapetsServicesResponse = {
  success: boolean;
  services?: unknown;
  error?: string;
  code?: string;
  isTestMode: boolean;
};

export type BatchSubQuery = {
  Id: string;
  ItemNumber?: string;
  Name?: string;
  BirthDate?: string;
  Countries?: string[];
};

export type BatchSearchResponse = {
  success: boolean;
  results?: TrapetsSearchResult[];
  /** On failure: the reason, in the request language. */
  error?: string;
  /** On failure: the stable error code (QL-718), e.g. `trapets_disabled`. */
  code?: string;
  isTestMode: boolean;
};

// ─── Trapets Status / Utils ──────────────────────────────

export type PEPRole = {
  title: string;
  roleType: string | null;
};

export type MatchedIndividual = {
  name: string | null;
  birthDate: string | null;
  countries: string[];
  listType: string;
};

export type MatchedEntity = {
  name: string | null;
  corporateId: string | null;
  countries: string[];
  listType: string;
};

export type TrapetsStatus = {
  isPEP: boolean;
  isSanctioned: boolean;
  pepSources: string[];
  pepRoles: PEPRole[];
  sanctionSources: string[];
  matchedIndividuals: MatchedIndividual[];
  matchedEntities: MatchedEntity[];
};

// Human-readable names for PEP sources
export const PEP_SOURCE_NAMES: Record<string, string> = {
  PEP_Edge: "PEP Edge",
  ACURIS_PEP_NN: "ION/Acuris PEP non-Nordic",
  ACURIS_PEP: "ION/Acuris PEP",
  DowJonesPep: "Dow Jones PEP list",
  DOW_JONES: "Dow Jones specially filtered PEP list",
};

// Human-readable names for Sanction sources
export const SANCTION_SOURCE_NAMES: Record<string, string> = {
  EU_GLOBAL: "EU Consolidated List of Financial Sanctions (FSD)",
  Instant_EU_Sanctions: "European Commission Official Journal List",
  UN_CONSOLIDATED: "United Nations Security Council Consolidated List",
  OFAC_CS: "OFAC Consolidated Sanctions List",
  OFAC: "OFAC Specially Designated Nationals List",
  HMTreasury: "OFSI Financial Sanctions Targets",
  UkSanctions: "UK Sanctions lists",
  Acuris_Sanction: "ION/Acuris Consolidated Sanctions",
  DowJonesSCO: "Dow Jones Sanction Control & Ownership",
  BelgianFPSF: "Belgian National Financial Sanctions",
  NetherlandsNTS: "Netherlands National Sanction List Terrorism",
  SwissSanctions: "Swiss Sanctions list",
  CKP: "Finnish Police list of freezed assets",
  FSA: "FSA Warning list",
};

export function getPEPSourceName(source: string): string {
  return PEP_SOURCE_NAMES[source] || source;
}

export function getSanctionSourceName(source: string): string {
  return SANCTION_SOURCE_NAMES[source] || source;
}

export function buildSSNWithPrefix(country: string, ssn: string): string {
  const cleanSSN = ssn.replace(/^[A-Z]{2}/, "");
  return `${country}${cleanSSN}`;
}

const ISO2_TO_ISO3: Record<string, string> = {
  NO: "NOR",
  SE: "SWE",
  DK: "DNK",
  FI: "FIN",
  GB: "GBR",
  US: "USA",
  DE: "DEU",
  FR: "FRA",
  NL: "NLD",
  BE: "BEL",
  CH: "CHE",
};

// Convert ISO 2-letter code to ISO 3-letter code for Trapets company search.
// An unknown code is returned as is.
export function getISO3Code(iso2: string): string {
  return ISO2_TO_ISO3[iso2] || iso2;
}

export function extractTrapetsStatus(results: TrapetsSearchResult[]): TrapetsStatus {
  let isPEP = false;
  let isSanctioned = false;
  const pepSources = new Set<string>();
  const pepRolesMap = new Map<string, PEPRole>();
  const sanctionSources = new Set<string>();
  const matchedIndividuals: MatchedIndividual[] = [];
  const matchedEntities: MatchedEntity[] = [];
  const seenIndividuals = new Set<string>();
  const seenEntities = new Set<string>();

  for (const result of results) {
    if (result.Individuals && Array.isArray(result.Individuals)) {
      for (const individual of result.Individuals) {
        const individualKey = `${individual.Name}-${individual.BirthDate}-${individual.ListType}`;
        if (!seenIndividuals.has(individualKey)) {
          seenIndividuals.add(individualKey);
          const countries: string[] = [];
          if (individual.Countries && Array.isArray(individual.Countries)) {
            for (const c of individual.Countries) {
              if (c.CountryCode) {
                countries.push(c.CountryCode);
              }
            }
          }
          matchedIndividuals.push({
            name: individual.Name || null,
            birthDate: individual.BirthDate || null,
            countries,
            listType: individual.ListType || "Unknown",
          });
        }

        if (individual.ListType === "PEP") {
          isPEP = true;
          if (individual.SourceName) {
            pepSources.add(individual.SourceName);
          }
          if (individual.Roles && Array.isArray(individual.Roles)) {
            for (const role of individual.Roles) {
              if (role.Title) {
                const key = `${role.Title}-${role.RoleType || ""}`;
                if (!pepRolesMap.has(key)) {
                  pepRolesMap.set(key, {
                    title: role.Title,
                    roleType: role.RoleType || null,
                  });
                }
              }
            }
          }
        }
        if (individual.ListType === "SANCTION") {
          isSanctioned = true;
          if (individual.SourceName) {
            sanctionSources.add(individual.SourceName);
          }
        }
      }
    }

    if (result.Entities && Array.isArray(result.Entities)) {
      for (const entity of result.Entities) {
        const entityKey = `${entity.Name}-${entity.CorporateId}-${entity.ListType}`;
        if (!seenEntities.has(entityKey)) {
          seenEntities.add(entityKey);
          const countries: string[] = [];
          if (entity.Countries && Array.isArray(entity.Countries)) {
            for (const c of entity.Countries) {
              if (c.CountryCode) {
                countries.push(c.CountryCode);
              }
            }
          }
          matchedEntities.push({
            name: entity.Name || null,
            corporateId: entity.CorporateId || null,
            countries: [...new Set(countries)],
            listType: entity.ListType || "Unknown",
          });
        }

        if (entity.ListType === "SANCTION") {
          isSanctioned = true;
          if (entity.SourceName) {
            sanctionSources.add(entity.SourceName);
          }
        }
      }
    }
  }

  return {
    isPEP,
    isSanctioned,
    pepSources: Array.from(pepSources),
    pepRoles: Array.from(pepRolesMap.values()),
    sanctionSources: Array.from(sanctionSources),
    matchedIndividuals,
    matchedEntities,
  };
}

// ─── Compliance API Types ────────────────────────────────

/** QL-349 — `ClientViewFilter` is defined once in `./client-list`; do not redeclare it here. */
export type ClientViewSummary = {
  id: string;
  name: string;
  description: string | null;
  filters: ClientViewFilter[];
  isStandard?: boolean;
};

import type { EffectiveScreeningStatus } from "./compliance-review";

export type ClientForBatch = {
  id: string;
  name: string;
  type: "Person" | "Company" | "Group";
  ssn: string | null;
  /** QL-c — the alternative identifier; screened when there is no SSN. */
  dateOfBirth: string | null;
  country: string | null;
  orgId: string | null;
  isValid: boolean;
  validationErrors: string[];
  /** QL-21 follow-up — set when this client CAN be screened but the PEP half
   *  will come back unassessed: a Company/Group with no registered
   *  representative. Distinct from `validationErrors`, which block the run. */
  pepGap: string | null;
  /** The representative who will be screened for PEP, when there is one. */
  representativeName: string | null;
  // QL-21 — where this client stands BEFORE the run. `isValid` is only about
  // having the fields needed to screen; this is the compliance standing, which
  // is what "status" means everywhere else in the compliance section.
  status: EffectiveScreeningStatus;
};

export type BatchLookupResult = {
  clientId: string;
  clientName: string;
  clientType: "Person" | "Company" | "Group";
  searchResult: TrapetsSearchResult | null;
  hasHits: boolean;
  // QL-21 — the two independent findings, derived server-side from the same
  // result the screening log row was written from, so the batch results table
  // and Clients review can never disagree about a client.
  // `isPep` is tri-state: null = the PEP question was not put to anyone.
  isPep: boolean | null;
  isSanctioned: boolean;
  /** Who the PEP finding is about — the representative for a Company/Group (several: the names, joined). */
  subject: ScreeningSubject;
  /** QL-654 — every person the PEP finding is about, in order (no national ids); empty when nobody was screened. */
  subjects?: ScreeningSubject[];
  // QL-21 — the client's standing AFTER this run was recorded, so the results
  // step can say what changed. Resolved from the freshly written cache, which
  // means it reflects a verdict that lapsed because these findings moved.
  status: EffectiveScreeningStatus;
  error?: string;
};

// ── The PEP / sanction answer a screening actually supports ──────────────────

/**
 * QL-g — the three answers a screening can give about one question.
 *
 * A CLOSED vocabulary: these exact strings are the conditional-display token
 * values (`__trapets_pep__`, `__trapets_sanctioned__`) offered in the PDF and
 * flow rule editors, and they are compared against rules already saved by
 * customers. Adding a fourth value would silently stop matching every existing
 * `== "no"` rule, so "we cannot say" reuses `unchecked`.
 */
export type TrapetsAnswer = "yes" | "no" | "unchecked";

/** The parts of a saved `trapets_lookup` snapshot these answers depend on. */
export type TrapetsSnapshotLike = {
  hasBeenChecked?: boolean;
  status?: TrapetsStatus | null;
  representativeStatus?: TrapetsStatus | null;
  subject?: { kind: "self" | "representative" | "none"; name?: string | null } | null;
  /** QL-654 — every screened representative; absent on snapshots saved before. */
  subjects?: Array<{ kind: "self" | "representative" | "none"; name?: string | null }> | null;
};

/**
 * Is the screened party a PEP?
 *
 * **`status.isPEP` is the wrong field for a Company or Group.** Trapets reports
 * no PEP for a legal entity at all, so the entity search always comes back
 * false — and reading it printed a confident "No" for a question nobody had
 * asked, on a document the client signs. PEP is a property of a natural person,
 * so for a company-like client the answer is the REPRESENTATIVE's search.
 *
 * With no representative registered there is nobody to ask, and the honest
 * answer is `unchecked` rather than `no`. That is the whole point of this
 * function: a false clear on a compliance document is far worse than an
 * admitted gap.
 *
 * Snapshots written before the representative screening existed carry no
 * `subject`; those are all Person screenings, where the client's own status is
 * the PEP answer.
 */
export function trapetsPepAnswer(snapshot: TrapetsSnapshotLike | null | undefined): TrapetsAnswer {
  if (!snapshot?.hasBeenChecked) return "unchecked";

  const subject = snapshot.subject;
  if (!subject || subject.kind === "self") {
    return snapshot.status?.isPEP ? "yes" : "no";
  }

  // A Company or Group with nobody registered to act for it: unanswerable.
  if (subject.kind === "none") return "unchecked";

  const rep = snapshot.representativeStatus;
  if (!rep) return "unchecked";
  return rep.isPEP ? "yes" : "no";
}

/**
 * Is the screened party sanctioned?
 *
 * Unlike PEP, this is the UNION of both searches: a legal entity genuinely can
 * be sanctioned, and so can the person acting for it. Either one is a match.
 *
 * No `subject.kind === "none"` gap here — the entity search still ran and is a
 * real answer on its own, for a Company. (A Group has no entity of its own to
 * search, so a Group with no representative is the one case this can still
 * under-report; it is also a client nothing else can screen either, and
 * `resolveScreeningPlan` refuses it upstream.)
 */
export function trapetsSanctionedAnswer(
  snapshot: TrapetsSnapshotLike | null | undefined,
): TrapetsAnswer {
  if (!snapshot?.hasBeenChecked) return "unchecked";
  const own = snapshot.status?.isSanctioned ?? false;
  const rep = snapshot.representativeStatus?.isSanctioned ?? false;
  return own || rep ? "yes" : "no";
}

/** Parse a stored snapshot JSON string, or null when it is absent/corrupt. */
export function parseTrapetsSnapshot(json: string | null | undefined): TrapetsSnapshotLike | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as TrapetsSnapshotLike;
  } catch {
    return null;
  }
}
